/**
 * The words in a chart's hover tip (Ari, 2026-10-08: "all charts need the
 * ability to hover over and see what the numbers are"). Pure: no React, no
 * DOM, no imports, so the api Jest proofs can load it
 * (apps/api/src/charts/chart-tip.web.claims.spec.ts).
 *
 * Two rules, proved by claim `charts.tip-exact`:
 * 1. A tip shows the exact figure the mark is drawn from. Never a compact
 *    axis figure ("$3K" for $2,500), never rounded past the cent.
 * 2. A missing or withheld value says so ("No data"), never $0 or 0.
 */

/** One tip: a bold head (what and when) and one line per figure. */
export interface TipContent {
  head: string;
  lines: string[];
}

const isNum = (v: number | null | undefined): v is number =>
  typeof v === "number" && Number.isFinite(v);

/** "1,234" with no rounding past two decimals. */
const grouped = (n: number, frac: number): string =>
  n.toLocaleString("en-US", {
    minimumFractionDigits: frac,
    maximumFractionDigits: frac,
  });

/**
 * Exact money: "$12,480", "$2,500.37", "−$41.20". Cents show whenever the
 * value has them. Null when there is no value.
 */
export const tipMoney = (v: number | null | undefined): string | null => {
  if (v == null || !Number.isFinite(v)) return null;
  const cents = Math.round(Math.abs(v) * 100);
  const sign = v < 0 && cents > 0 ? "−$" : "$";
  return sign + grouped(cents / 100, cents % 100 === 0 ? 0 : 2);
};

/** Exact count: "1,234 units", "1 unit". Null when there is no value. */
export const tipCount = (
  v: number | null | undefined,
  one?: string,
  many?: string,
): string | null => {
  if (!isNum(v)) return null;
  const n = Math.round(v * 100) / 100;
  const text = grouped(n, Number.isInteger(n) ? 0 : 2);
  if (!one) return text;
  return `${text} ${n === 1 ? one : (many ?? `${one}s`)}`;
};

/** A percent given as 0–100: "37.4%" (one decimal). Null when none. */
export const tipPct = (v: number | null | undefined): string | null =>
  isNum(v) ? `${grouped(Math.round(v * 10) / 10, 1)}%` : null;

/** "Nordstrom $1,234", or "Nordstrom: no data" when the value is missing. */
export const tipFact = (label: string, value: string | null): string =>
  value == null ? `${label}: no data` : `${label} ${value}`;

/** The line for a mark whose value is missing: "No data for this week". */
export const noDataLine = (span: string): string => `No data for this ${span}`;

/** Builds a tip; falsy lines are left out. */
export const chartTip = (
  head: string,
  lines: Array<string | null | false | undefined>,
): TipContent => ({
  head,
  lines: lines.filter((l): l is string => typeof l === "string" && l !== ""),
});

/** The whole tip as one sentence, for screen readers. */
export const tipText = (c: TipContent): string =>
  c.lines.length ? `${c.head}: ${c.lines.join(", ")}` : c.head;

/** Attributes that make an element a chart mark (spread on an SVG `<g>`/
 *  `<rect>` or an HTML element); the nearest `ChartTips` shows its tip. */
export interface TipMarkProps {
  "data-tip-mark": "";
  "data-tip": string;
  role: "img";
  "aria-label": string;
}

export const tipMark = (c: TipContent): TipMarkProps => ({
  "data-tip-mark": "",
  "data-tip": JSON.stringify(c),
  role: "img",
  "aria-label": tipText(c),
});

const TIP_PAD = 8;

/** Where the tip goes: centred above the mark, below when the top has no
 *  room, always at least TIP_PAD inside the viewport. Exported for the test. */
export const placeTip = (
  mark: { left: number; top: number; width: number; bottom: number },
  tip: { width: number; height: number },
  view: { width: number; height: number },
): { x: number; y: number } => {
  let x = mark.left + mark.width / 2 - tip.width / 2;
  let y = mark.top - tip.height - TIP_PAD;
  if (y < TIP_PAD) y = mark.bottom + TIP_PAD;
  if (y + tip.height > view.height - TIP_PAD)
    y = Math.max(TIP_PAD, view.height - tip.height - TIP_PAD);
  x = Math.max(TIP_PAD, Math.min(x, view.width - tip.width - TIP_PAD));
  return { x: Math.round(x), y: Math.round(y) };
};

